import { green, red, yellow } from '@ant-design/colors';
import { Progress, Table, TableColumnsType, Tooltip, Typography, theme } from 'antd';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSingleCompanySetting from '../../hooks/adminPage/useSingleCompanySetting';
import useReports from '../../hooks/dashboardPage/useReports';
import MiniTag from '../MiniTag';
import TimeDisplayForReport from '../TimeDisplayForReport';
import ReportCard from './ReportCard';

const agentStatus = [
    { text: ['Uzakta', 'Mola', 'Öğle arası'], colorIndex: 1 },
    {
        text: [
            'Ofiste',
            'Çevrimiçi',
            'Sohbet',
            'Tüm çağrılar',
            'Gelen çağrılar',
            'Giden çağrılar',
            'Tüm çağrılar + Chat',
            'Gelen çağrılar + Chat',
            'Giden çağrılar + Chat',
            'Çağrı sonrası + Chat'
        ],
        colorIndex: 2
    },
    { text: ['Çevrimdışı'], colorIndex: 3 },
    { text: ['Çağrı sonrası'], colorIndex: 4 },
    { text: ['New'], colorIndex: 5 },
    { text: ['Open'], colorIndex: 6 },
    { text: ['Pending'], colorIndex: 7 },
    { text: ['On Hold'], colorIndex: 8 },
    { text: ['Solved'], colorIndex: 9 },
    { text: ['Closed'], colorIndex: 10 }
];

interface ReportTableProps {
    /**
     * @refreshInterval number of seconds to refresh data in given prop (url)
     * @description it will only refresh data when the time passes, it will never refresh when 0 (only when re-rendered)
     */
    refreshInterval?: number;
    /**
     * @agentSearchable if table have 'agentName' column, allow to search agent by name.
     */
    agentSearchable?: boolean;
    title: string;
    /**
     * @url the data will be loaded from this url
     */
    url: string;
}

type ReportTableData = {
    [key: string]: string | number;
};

/**
 * This function sorts an array of time strings in ascending order by converting them to Date objects
 * and comparing their getTime values.
 * @param {string} a - a is a string representing a time in the format "DD:HH:MM:SS", where DD
 * represents the number of days, HH represents the number of hours, MM represents the number of
 * minutes, and SS represents the number of seconds.
 * @param {string} b - The parameter "b" is a string representing a time in the format of
 * "DD:HH:MM:SS", where DD represents the number of days, HH represents the number of hours, MM
 * represents the number of minutes, and SS represents the number of seconds.
 * @returns the difference between the time values of two strings in milliseconds. Specifically, it is
 * converting the time values in the strings to Date objects, and then returning the difference between
 * the getTime() values of those Date objects.
 */
const handleTimeStringSort = (a: string, b: string) => {
    if (typeof a !== 'string' || typeof b !== 'string') return 0;
    const [aDay, aHour, aMinute, aSecond] = a.split(':');
    const [bDay, bHour, bMinute, bSecond] = b.split(':');

    const aDate = new Date();
    aDate.setHours(Number(aDay) * 24 + Number(aHour), Number(aMinute), Number(aSecond));
    const bDate = new Date();
    bDate.setHours(Number(bDay) * 24 + Number(bHour), Number(bMinute), Number(bSecond));
    return aDate.getTime() - bDate.getTime();
};

export default function ReportTable({ refreshInterval = 0, agentSearchable = false, title, url }: ReportTableProps) {
    const {
        query: { reportType }
    } = useRouter();
    const {
        token: { colorSuccess, colorWarning, colorError, colorBorder, colorPrimary }
    } = theme.useToken();
    const { t } = useTranslation('dashboard');
    const { data: zoneData } = useSingleCompanySetting('TIMEZONE');
    const zone = zoneData?.value;

    const reportUrl = reportType ? url : zone ? `${url}?zone=${zone}` : null;

    const { data: reportData, isLoading, isValidating, mutate } = useReports<'table'>(reportUrl, refreshInterval);
    const updatedAt = useMemo(() => (reportData && !isValidating ? Date.now() : null), [reportData, isValidating]);

    const [searchTerm, setSearchTerm] = useState<string>('');

    const dataSource = useMemo(
        () =>
            reportData?.data
                ?.map(
                    (valueList, indexKey) =>
                        reportData?.columns.reduce<ReportTableData>(
                            (prevValue, columnName, index) => ({ ...prevValue, [columnName]: valueList[index] }),
                            { indexKey: indexKey + 1 } as ReportTableData
                        )
                )
                .filter((value) => {
                    if (agentSearchable) {
                        if (value['agentName']) return value['agentName'].toString().toLowerCase().includes(searchTerm);
                        if (value['fullName']) return value['fullName'].toString().toLowerCase().includes(searchTerm);
                    } else return true;
                }),
        [agentSearchable, searchTerm, reportData]
    );
    const setStatusColor = (text: string) => {
        const colorIndex = agentStatus.find((status) => {
            return status.text.includes(text);
        })?.colorIndex;
        switch (colorIndex) {
            case 1:
                return colorWarning;
            case 2:
                return colorSuccess;
            case 3:
                return colorError;
            case 4:
                return colorPrimary;
            default:
                return colorBorder;
        }
    };
    const columns: TableColumnsType<ReportTableData> = reportData?.columns?.reduce(
        (acc, c, i) => [
            ...acc,
            {
                title: t(`reportTable.columns.${c}` as any),
                dataIndex: c,
                key: c,
                ...(i === 0 ? { fixed: 'left', width: 150 } : {}),
                ellipsis: { showTitle: false },
                sorter: (a, b) => {
                    if (typeof a[c] === 'string' && typeof b[c] === 'string') return a[c].localeCompare(b[c]);
                    if (typeof a[c] === 'number' && typeof b[c] === 'number') return a[c] - b[c];
                },
                ...(c.includes('Status')
                    ? {
                          render: (value) => {
                              return (
                                  <Tooltip color={setStatusColor(value)} placement="topRight" title={value}>
                                      {c.includes('ticketStatus') ? (
                                          <MiniTag status={value} centered />
                                      ) : (
                                          <Typography.Text
                                              style={{
                                                  color: setStatusColor(value)
                                              }}
                                          >
                                              {value}
                                          </Typography.Text>
                                      )}
                                  </Tooltip>
                              );
                          }
                      }
                    : c.includes('Ratio')
                    ? {
                          render: (value) => (
                              <Progress
                                  percent={value}
                                  status="active"
                                  steps={5}
                                  size="small"
                                  strokeColor={[green[6], green[4], yellow[5], red[3], red[5]]}
                              />
                          )
                      }
                    : c.includes('Duration')
                    ? {
                          render: (value: string) => <TimeDisplayForReport timeText={value} />,
                          sorter: (a, b) => handleTimeStringSort(a[c], b[c])
                      }
                    : {})
            }
        ],
        [
            {
                title: t('reportTable.columns.index'),
                dataIndex: 'indexKey',
                key: 'indexKey',
                fixed: 'left',
                width: 70
            }
        ]
    );

    return (
        <ReportCard
            fixedHeight
            title={title}
            updateTime={updatedAt}
            loading={false}
            onButtonClick={mutate}
            agentSearchable={agentSearchable ? { setSearchTerm } : null}
        >
            <Table<ReportTableData>
                columns={columns}
                dataSource={dataSource}
                id="report-table"
                loading={isLoading}
                pagination={false}
                scroll={{ x: 800, y: 339 }}
                size="small"
                tableLayout="fixed"
                rowKey="indexKey"
            />
        </ReportCard>
    );
}
